import React from 'react';
import {Route, useLocation} from 'react-router-dom';
import Helmet from 'react-helmet';
import routeMapping from './routeMapping.json';
import PalStyleWrapperPage from '../pages/PalStyleWrapper';
import LandingPageStyleWrapperPage from '../pages/LandingPage/StyleWrapper';
import MedicalNecessityChecklist from '../pages/MedicalNecessityChecklist';
import PrivateRoute from '../components/PrivateRoute';

export const NotFoundPage = () => <h3>Not Found</h3>;

export const COMPONENT_MAPPING = {
  PAL: PalStyleWrapperPage,
  LANDINGPAGE: LandingPageStyleWrapperPage,
  MEDICALNECESSITYCHECKLIST: MedicalNecessityChecklist
};

/**
 * Checks whether a property exists and is truthy within the settings object by evaluating a dot-separated path
 *
 * @param {Object} settings - The object to search within.
 * @param {string} path - The dot-separated string representing the path to the desired property.
 * @returns {boolean} - `true` if the property exists and is truthy, otherwise `false`.
 */
const evaluatePropertyPath = (settings, path) => {
  // Special handling for "authenticate" to support both legacy and loginV2.authenticate
  if (path === 'authenticate') {
    // Check loginV2.authenticate first, fallback to legacy authenticate
    if (settings.loginV2 && settings.loginV2.hasOwnProperty('authenticate')) {
      return settings.loginV2.authenticate === true;
    }
    return settings.authenticate === true;
  }

  const keys = path.split('.');
  let currentValue = settings;

  for (const key of keys) {
    if (currentValue && currentValue.hasOwnProperty(key)) {
      currentValue = currentValue[key];
    } else {
      return false;
    }
  }

  return Boolean(currentValue);
}

/**
   * Evaluates a logical condition composed of nested property paths within the settings.
   *
   * @param {string} condition - A string containing one or more dot-separated property paths joined by logical operators.
   * @param {Object} settings - The object containing the settings to evaluate.
   * @returns {boolean} - `true` if all conditions are met, otherwise `false`.
   */
const evaluateCondition = (condition, settings) => {
  try {
    const conditionParts = condition.split(' && ').map((part) => part.trim());

    return conditionParts.every((part) => {
      if (part.startsWith('siteType')) {
        // Custom logic to evaluate siteType conditions
        const [key, value] = part.split('===').map((str) => str.trim().replace(/['"]/g, ''));
        return settings[key] && settings[key].toLowerCase() === value.toLowerCase();
      }

      if (part.startsWith('!')) {
        const negatedPart = part.slice(1);
        return !evaluatePropertyPath(settings, negatedPart);
      }

      return evaluatePropertyPath(settings, part);
    });
  } catch (error) {
    return false;
  }
};

const renderRoutes = (settings) => (
  routeMapping.routes.map((route) => {

    const shouldRenderComponent = route['criteriaToRenderPath'] ? evaluateCondition(route['criteriaToRenderPath'], settings) : true;

    // Check authentication condition - supports both legacy and loginV2.authenticate
    let authenticate = false;
    if (route['checkAuthenticationCondition']) {
      // Check loginV2.authenticate first, fallback to legacy authenticate
      if (settings.loginV2 && settings.loginV2.hasOwnProperty('authenticate')) {
        authenticate = settings.loginV2.authenticate === true;
      } else {
        authenticate = settings.authenticate === true;
      }
    }

    if (!shouldRenderComponent) {
      return null;
    }

    let wrapperComponent = NotFoundPage;

    // Handle wrapperComponentToRender (e.g., MedicalNecessityChecklist)
    if (route['wrapperComponentToRender'] && COMPONENT_MAPPING[route['wrapperComponentToRender'].toUpperCase()]) {
      wrapperComponent = COMPONENT_MAPPING[route['wrapperComponentToRender'].toUpperCase()];
    }

    // Fall back to siteType wrapper if no wrapper was selected
    if (wrapperComponent === NotFoundPage && settings.siteType) {
      wrapperComponent = COMPONENT_MAPPING[settings.siteType.toUpperCase()];
    }

    if (authenticate) {
      return (
        <PrivateRoute
          key={route.path}
          exact={route.exact}
          path={route.path}
          component={wrapperComponent}
        />
      );
    } else if (shouldRenderComponent) {
      return (
        <Route
          key={route.path}
          exact={route.exact}
          path={route.path}
          component={wrapperComponent}
        />
      );
    }
    return null;
  })
);

// helper component to render additional nested helmet component for routes with alternate titles
// alternate titles should override title from parent helmet components
const RouteTitle = ({pageTitle}) => {
  const {pathname} = useLocation();
  const route = routeMapping.routes.find((r) => r.path === pathname);
  return route?.titlePrefix ? <Helmet><title>{`${route.titlePrefix}${pageTitle}`}</title></Helmet> : null;
};

export {evaluatePropertyPath, evaluateCondition, renderRoutes, RouteTitle}
